import { useAppSelector } from 'hooks/reduxHooks'
import { useEffect, useState } from 'react'

interface UserLocation {
  latitude: number | undefined
  longitude: number | undefined
  loading: boolean
}

/**
 * Hook that gets user location with fallback strategy:
 * 1. First tries server-provided coordinates from Redux state
 * 2. If not available, falls back to browser geolocation
 */
export function useUserLocation(): UserLocation {
  const serverLatitude = useAppSelector(state => state.geo.latitude)
  const serverLongitude = useAppSelector(state => state.geo.longitude)
  const [browserLocation, setBrowserLocation] = useState<{ latitude: number, longitude: number } | null>(null)
  const [loading, setLoading] = useState(false)

  // If no server coordinates, try browser geolocation
  useEffect(() => {
    if (!serverLatitude && !serverLongitude && !browserLocation && !loading) {
      setLoading(true)

      if (navigator?.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setBrowserLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            })
            setLoading(false)
          },
          (error) => {
            console.warn('Browser geolocation failed:', error)
            setLoading(false)
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 60000
          }
        )
      } else {
        setLoading(false)
      }
    }
  }, [serverLatitude, serverLongitude, browserLocation, loading])

  // If server coordinates are available, use them
  if (serverLatitude && serverLongitude) {
    return {
      latitude: serverLatitude,
      longitude: serverLongitude,
      loading: false
    }
  }

  return {
    latitude: browserLocation?.latitude,
    longitude: browserLocation?.longitude,
    loading
  }
}
